import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeaturedTeachersCarousel from '../components/FeaturedTeachersCarousel';
import TeacherSpotlightCard from '../components/TeacherSpotlightCard';
import PublicSiteHeader from '../components/PublicSiteHeader';
import PlatformFooter from '../components/PlatformFooter';
import { teacherProfileService } from '../services/api';
import { EmptyState, Spinner } from '../components/ui';
import useTenantStore from '../store/tenantStore';

const asList = (data) => Array.isArray(data) ? data : data?.results || [];

export default function TeachersDirectory() {
  const { t } = useTranslation();
  const featuredContentEnabled = useTenantStore((state) => state.isFeatureEnabled('featured_content'));
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const loadAllTeachers = async () => {
      try {
        const first = await teacherProfileService.list({ page: 1 });
        let list = asList(first.data);
        const total = first.data?.count || list.length;
        const pageSize = list.length || 20;
        const pageCount = Math.ceil(total / pageSize);
        if (pageCount > 1) {
          const remaining = await Promise.all(
            Array.from({ length: pageCount - 1 }, (_, index) => teacherProfileService.list({ page: index + 2 }))
          );
          list = list.concat(...remaining.map((response) => asList(response.data)));
        }
        if (active) setTeachers(list);
      } catch (error) {
        if (active) setTeachers([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadAllTeachers();
    return () => { active = false; };
  }, []);

  const featured = useMemo(() => teachers.filter((teacher) => teacher.is_featured), [teachers]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return teachers;
    return teachers.filter((teacher) => {
      const user = teacher.user || {};
      return [
        user.first_name, user.last_name, teacher.bio, teacher.qualifications,
        ...(Array.isArray(teacher.specializations) ? teacher.specializations : []),
      ].filter(Boolean).join(' ').toLocaleLowerCase().includes(query);
    });
  }, [search, teachers]);

  return (
    <div className="h-screen overflow-y-auto bg-surface-50 pb-20 text-gray-900">
      <PublicSiteHeader active="teachers" />

      <main>
        {featuredContentEnabled && featured.length > 0 && (
          <section className="relative overflow-hidden border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-cyan-50 py-16 sm:py-20">
            <div className="absolute -right-32 -top-24 h-80 w-80 rounded-full bg-primary-200/35 blur-3xl" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6">
              <FeaturedTeachersCarousel
                teachers={featured}
                title={t('Famous teachers')}
                subtitle={t('Meet the educators highlighted for the quality and value they bring to learners.')}
              />
            </div>
          </section>
        )}

        <section className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="mx-auto mb-10 max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">{t('Teacher directory')}</span>
            <h1 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t('Find the right teacher for you')}</h1>
            <p className="mt-3 text-gray-600">{t('Search by teacher name, subject, qualification or keyword.')}</p>
            <div className="relative mt-7">
              <svg className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 rtl:left-auto rtl:right-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.35-4.35m1.35-5.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" /></svg>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('Search teachers...')}
                className="w-full rounded-2xl border border-surface-200 bg-white py-4 pl-12 pr-4 text-sm shadow-card outline-none transition placeholder:text-gray-400 focus:border-primary-400 focus:ring-4 focus:ring-primary-100 rtl:pl-4 rtl:pr-12"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-surface-200 bg-white py-10"><EmptyState title={t('No teachers found')} description={t('Try a different name or keyword.')} /></div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((teacher) => <TeacherSpotlightCard key={teacher.id} teacher={teacher} compact />)}
            </div>
          )}
        </section>
      </main>
      <PlatformFooter />
    </div>
  );
}
